import React, { useState, useEffect } from 'react'
import { Users, Loader2 } from 'lucide-react'
import { Drawer } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useContactStore from '../../stores/contactStore'
import useContactRoleStore from '../../stores/contactRoleStore'
import usePartnerStore from '../../stores/partnerStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { ContactStatus } from '../../types/contact'
import { isValidEmail, isValidPhone } from '../../utils/validation'

interface AddContactDrawerProps {
  isOpen: boolean
  onClose: () => void
  preselectedPartner?: { id: string; name: string } | null
  onContactCreated?: () => void
}

const AddContactDrawer: React.FC<AddContactDrawerProps> = ({
  isOpen,
  onClose,
  preselectedPartner,
  onContactCreated
}) => {
  const { showSuccess, showError } = useToast()
  const { createContact, loadContacts } = useContactStore()
  const { roles, loadRoles } = useContactRoleStore()
  const { partners, loadPartners } = usePartnerStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [partnerId, setPartnerId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [roleId, setRoleId] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [status, setStatus] = useState<ContactStatus>('Active')
  const [isCreating, setIsCreating] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')

  // Set partner ID when preselectedPartner is provided
  useEffect(() => {
    if (isOpen && preselectedPartner) {
      setPartnerId(preselectedPartner.id)
    }
  }, [isOpen, preselectedPartner])

  // Load roles and partners when drawer opens
  useEffect(() => {
    if (isOpen && selectedCustomer?.id) {
      loadRoles(true) // Only load active roles
      // Only load partners if no preselected partner (to populate dropdown)
      if (!preselectedPartner) {
        loadPartners(String(selectedCustomer.id))
      }
    }
  }, [isOpen, loadRoles, loadPartners, selectedCustomer, preselectedPartner])

  const resetForm = () => {
    setPartnerId('')
    setFirstName('')
    setLastName('')
    setRoleId('')
    setJobTitle('')
    setEmail('')
    setPhone('')
    setIsPrimary(false)
    setStatus('Active')
    setEmailError('')
    setPhoneError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    if (!partnerId) {
      showError('Validation Error', 'Sponsor is required')
      return
    }

    if (!firstName.trim()) {
      showError('Validation Error', 'First name is required')
      return
    }

    if (firstName.length > 255) {
      showError('Validation Error', 'First name must be 255 characters or less')
      return
    }

    if (!lastName.trim()) {
      showError('Validation Error', 'Last name is required')
      return
    }

    if (lastName.length > 255) {
      showError('Validation Error', 'Last name must be 255 characters or less')
      return
    }

    if (!roleId) {
      showError('Validation Error', 'Role is required')
      return
    }

    if (email && !isValidEmail(email)) {
      showError('Validation Error', 'Please enter a valid email address')
      return
    }

    if (phone && !isValidPhone(phone)) {
      showError('Validation Error', 'Please enter a valid phone number')
      return
    }

    setIsCreating(true)

    try {
      const newContact = await createContact(
        String(selectedCustomer.id),
        {
          partner_id: partnerId,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          role_id: roleId,
          job_title: jobTitle.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          is_primary: isPrimary,
          status
        }
      )

      showSuccess('Contact Created', `"${newContact.first_name} ${newContact.last_name}" has been created successfully`)

      // Reload contacts list to get the full contact data with nested objects
      await loadContacts(String(selectedCustomer.id))

      // Call the callback if provided (for PartnerDetailDrawer)
      onContactCreated?.()

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to create contact',
        errorMessage.includes('already exists')
          ? 'A contact with this information already exists'
          : 'Please try again later'
      )
    } finally {
      setIsCreating(false)
    }
  }

  const validateEmail = (emailValue: string) => {
    if (emailValue && !isValidEmail(emailValue)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    // Clear error when user starts typing again
    if (emailError && (!value || isValidEmail(value))) {
      setEmailError('')
    }
  }

  const validatePhone = (phoneValue: string) => {
    if (phoneValue && !isValidPhone(phoneValue)) {
      setPhoneError('Please enter a valid phone number')
    } else {
      setPhoneError('')
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPhone(value)
    // Clear error when user starts typing again
    if (phoneError && (!value || isValidPhone(value))) {
      setPhoneError('')
    }
  }

  // Get active roles and partners
  const activeRoles = roles.filter((r) => r.is_active)
  const activePartners = partners.filter((p) => p.status === 'Active')

  // Check if partner is locked (preselected)
  const isPartnerLocked = !!preselectedPartner

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Contact"
      description="Create a new contact for a sponsor"
      icon={<Users className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Partner */}
        {isPartnerLocked ? (
          <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
            <span className="font-semibold">{preselectedPartner.name}</span>
          </div>
        ) : (
          <div>
            <label htmlFor="partner" className="block text-xs font-medium text-gray-700 mb-1">
              Sponsor <span className="text-red-500">*</span>
            </label>
            <select
              id="partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select sponsor</option>
              {activePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* First Name + Last Name Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="first-name" className="block text-xs font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              maxLength={255}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="last-name" className="block text-xs font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              maxLength={255}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Role + Job Title Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="role" className="block text-xs font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select role</option>
              {activeRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="job-title" className="block text-xs font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              id="job-title"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Marketing Manager"
              maxLength={255}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Email + Phone Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => validateEmail(email)}
              placeholder="john@example.com"
              maxLength={255}
              disabled={isCreating}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${emailError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {emailError && (
              <p className="text-xs text-red-500 mt-0.5">{emailError}</p>
            )}
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id="phone"
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={() => validatePhone(phone)}
              placeholder="+1 (555) 123-4567"
              maxLength={50}
              disabled={isCreating}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${phoneError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {phoneError && (
              <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>
            )}
          </div>
        </div>

        {/* Primary + Status Row */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="flex items-center gap-2">
            <input
              id="is-primary"
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              disabled={isCreating}
              className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is-primary" className="text-xs text-gray-700">
              Primary contact
            </label>
          </div>
          <div>
            <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContactStatus)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating || !partnerId || !firstName.trim() || !lastName.trim() || !roleId || !!emailError || !!phoneError}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Create Contact
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default AddContactDrawer
